import React, { FC, useCallback } from 'react';

import classNames from 'classnames';

import { EvFlexRow, EvSection, EvTitle } from '@evinced-private/ui-common';

import {
	WFA_DASHBOARD_PAGE_TABLE_SECTION_ID,
	WFA_DASHBOARD_PAGE_TABLE_TITLE_ID
} from 'data-automation-ids/WfaDashboardAutomationIds';
import { IWfaSessionRow } from 'src/apps/wfa/services/WfaDashboardServiceTypes';
import { DeletingIndicator } from 'src/common/components/deleting-indicator';
import { LabelsManagementTableActions } from 'src/common/components/labels-management-table-actions';
import { useWebConfiguredValues } from 'src/common/components/labels-management-table-actions/hooks';
import { PlatformTable } from 'src/common/components/platform-table/PlatformTable';
import { TableSkeleton } from 'src/common/components/skeletons/table-skeleton/TableSkeleton';
import { useDeleteRun } from 'src/common/hooks/useDeleteRun';
import { WFA_SESSIONS_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { usePlatformConfigStore } from 'src/common/stores/platformConfigStore';

import {
	ADD_LABELS_BUTTON_LABEL,
	CHANGE_LABEL_VALUE_BUTTON_LABEL,
	DATA_TYPE,
	DELETING_SESSION_MESSAGE,
	REMOVE_LABELS_BUTTON_LABEL,
	SELECT_SESSIONS_PROMPT,
	TABLE_CAPTION,
	TABLE_KEY_FIELD,
	TABLE_TITLE
} from './WfaSessions.constants';
import { IWfaDashboardTableProps } from './WfaSessions.types';
import { getWfaSessionsColumns } from './WfaSessionsTableHelper';

import './WfaSessionsTable.scss';

export const WfaSessionsTable: FC<IWfaDashboardTableProps> = ({
	sessionsQuery,
	unfilteredTotal,
	isNewPlatformEnabled,
	unitIdDisplayName,
	unitIdLabelConfig,
	environmentDisplayName,
	environmentLabelConfig,
	selectedRows,
	onSelectRow,
	onSelectAll,
	onClearSelection
}) => {
	const total: number = sessionsQuery.data?.total ?? 0;
	const { tenantConfiguredLabels } = usePlatformConfigStore();
	const configuredValues = useWebConfiguredValues();
	const { deleteRun: deleteSession, isDeleting } = useDeleteRun('wfa');

	const isTableLoading =
		sessionsQuery.isLoading ||
		sessionsQuery.isPreviousData ||
		(sessionsQuery.isStale && sessionsQuery.isRefetching);

	const handleProcessingComplete = useCallback(async () => {
		await sessionsQuery.refetch();
		onClearSelection();
	}, [sessionsQuery, onClearSelection]);

	return (
		<EvSection
			ariaLabel="Web Flow Analyzer sessions Table"
			skipLinkId="main-section"
			className={classNames('dashboard-section', 'wfa-sessions-table')}
			dataAutomationId={WFA_DASHBOARD_PAGE_TABLE_SECTION_ID}
		>
			<EvFlexRow align="CENTER" gap="MEDIUM">
				<EvTitle
					titleText="Uploaded sessions"
					headingLevel={2}
					className="page-heading"
					dataAutomationId={WFA_DASHBOARD_PAGE_TABLE_TITLE_ID}
				/>
				{isDeleting && !isNewPlatformEnabled && (
					<DeletingIndicator message={DELETING_SESSION_MESSAGE} />
				)}
			</EvFlexRow>

			{isNewPlatformEnabled && (
				<LabelsManagementTableActions<IWfaSessionRow>
					selectedRows={selectedRows}
					handleClearSelection={onClearSelection}
					promptText={SELECT_SESSIONS_PROMPT}
					itemName="session"
					addLabelButtonLabel={ADD_LABELS_BUTTON_LABEL}
					changeLabelValueButtonLabel={CHANGE_LABEL_VALUE_BUTTON_LABEL}
					removeLabelsButtonLabel={REMOVE_LABELS_BUTTON_LABEL}
					onLabelActionSuccess={handleProcessingComplete}
					tenantConfiguredLabels={tenantConfiguredLabels}
					configuredValues={configuredValues}
					appType="wfa"
					deleteProcessingMessage={isDeleting ? DELETING_SESSION_MESSAGE : null}
				/>
			)}
			{isTableLoading ? (
				<TableSkeleton columns={6} rowCount={7} />
			) : (
				<PlatformTable
					isTableLoading={isTableLoading}
					tableId={WFA_SESSIONS_TABLE_ID}
					tableData={sessionsQuery.data?.rows}
					totalPaginationableResults={total}
					totalCount={unfilteredTotal}
					persistTableState
					columns={getWfaSessionsColumns(
						isNewPlatformEnabled,
						unitIdDisplayName,
						unitIdLabelConfig,
						environmentDisplayName,
						environmentLabelConfig,
						deleteSession,
						selectedRows.length > 1 || isDeleting
					)}
					options={{
						keyField: TABLE_KEY_FIELD,
						title: TABLE_TITLE,
						caption: TABLE_CAPTION,
						pagination: true,
						dataType: DATA_TYPE,
						remote: true,
						selectRow: isNewPlatformEnabled
							? {
									mode: 'checkbox',
									selected: selectedRows.map((r) => r.sessionId),
									onSelect: onSelectRow,
									onSelectAll
								}
							: undefined
					}}
				/>
			)}
		</EvSection>
	);
};
